import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Empty, Table, message } from 'antd';
import { makeStyles } from '@material-ui/styles';
import { dayFormatType, formatDay } from 'common/helpers/day';
import { orderStage, orderSecondaryStage } from 'common/constants/booking';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { QUERY_DIFFERENT_STAGE_ORDERS } from './queries';

import generatePath from '~settings/routing';

import { useLazyQuery } from '@apollo/client';

const useStyles = makeStyles(theme => ({
  loading: {
    height: '80vh',
  },

  pendingBoxHint: {
    background: '#FF4D4F',
    ...theme.typography.body5,
    padding: '0px 6px',
    borderRadius: 10,
    color: theme.colors.gray1,
  },

  pendingBoxTitle: {
    ...theme.typography.h5,
    display: 'inline-block',
    color: theme.colors.titleColor,
  },
  row: {
    background: theme.colors.gray1,
    marginTop: 16,

    padding: '0 24px 24px',

    '& .ant-table-thead': {
      height: '48px!important',
      '&>tr': {
        '&>th': {
          padding: '13px 12px 13px 12px',
        },
      },
    },
    '& .ant-table-tbody > tr > td': {
      padding: '13px 12px 13px 12px',
    },
    '& .ant-empty-normal': {
      margin: '64px 0',
    },
    '& .ant-table-row': {
      '&:hover': {
        cursor: 'pointer',
      },
    },
  },
  circleIcon: {
    width: 6,
    height: 6,
    background: '#FA8C16',
    display: 'inline-block',
    marginRight: 8,
    borderRadius: '50%',
    verticalAlign: 2,
  },
  blod: {
    ...theme.typography.body2,
    color: theme.colors.titleColor,
    textTransform: 'capitalize',
  },
  title: {
    paddingTop: 17,
    paddingBottom: 17,
  },
  more: {
    paddingBottom: 0,
  },
}));

const PendingCheckIn = ({ order }) => {
  const classes = useStyles();
  const history = useHistory();
  const [orderInfo, setOrderInfo] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [current, setCurrent] = useState(1);

  const formatOrder = data => {
    const { totalCount, edges } = data;
    const orders = edges?.map(i => i.node);
    setOrderInfo(orders);
    setTotalCount(totalCount);
  };

  useEffect(() => {
    formatOrder(order);
  }, [order]);

  const { t } = useTranslation();
  const openOrderDetailsPage = record => {
    history.push(
      generatePath(
        'orders.detail',
        { id: record?.referenceId },
        { from: 'Dashboard|Pending Check-in' },
      ),
    );
  };
  const isShowCounterOffer = order => {
    const isPendingCounterOffer =
      order.secondaryStage === orderSecondaryStage.PENDING_COUNTER_OFFER;

    const isOverTimeCounterOffer =
      order.secondaryStage === orderSecondaryStage.COUNTER_OFFER_NON_CONFIRM_IN_TIME;

    const isRejectAll =
      order.secondaryStage === orderSecondaryStage.REJECT_BY_LANDLORD &&
      order.stage === orderStage.LOST &&
      !!order?.counterOffers?.length;
    return isPendingCounterOffer || isOverTimeCounterOffer || isRejectAll;
  };
  const studentNameField = record => {
    const { passportFirstName, passportMiddleName, passportLastName } = record?.student ?? {};
    let studentName = '-';

    if (passportFirstName) {
      studentName = passportFirstName;
    }

    if (passportMiddleName) {
      studentName =
        studentName === '-' ? passportMiddleName : studentName.concat(' ', passportMiddleName);
    }
    if (passportLastName) {
      studentName =
        studentName === '-' ? passportLastName : studentName.concat(' ', passportLastName);
    }

    return studentName;
  };

  const columns = [
    {
      title: (
        <span className={classes.blod}>{t('landlord.long_tail.booking.booking_number.field')}</span>
      ),
      dataIndex: 'id',
      key: 'id',
      width: '20%',
      render: (text, record) => <span>{record?.referenceId || '-'}</span>,
    },
    {
      title: (
        <span className={classes.blod}>{t('landlord.long_tail.booking.student_name.field')}</span>
      ),
      dataIndex: 'studentName',
      key: 'studentName',
      width: '23%',
      render: (text, record) => <span>{studentNameField(record)}</span>,
    },
    {
      title: (
        <span className={classes.blod}>{t('landlord.payment.list.column.property_name')}</span>
      ),
      dataIndex: 'propertyName',
      key: 'propertyName',
      width: '22%',
      render: (text, record) => <span>{record?.property ? `${record.property.name} ` : '-'}</span>,
    },

    {
      title: (
        <span className={classes.blod}>{t('landlord.long_tail.booking.move_in_date.field')}</span>
      ),
      dataIndex: 'MoveInDate',
      key: 'MoveInDate',
      width: '18%',
      render: (text, row) => {
        let moveInDate = isShowCounterOffer(row)
          ? row.counterOffers?.[0]?.moveInDate
          : row?.moveInDate;

        return moveInDate ? formatDay(dayFormatType.CONTENTDATE, moveInDate) : '-';
      },
    },
    {
      title: (
        <span className={classes.blod}>{t('landlord.long_tail.booking.move_out_date.field')}</span>
      ),
      dataIndex: 'moveOutDate',
      key: 'moveOutDate',
      width: '18%',
      render: (text, row) => {
        let moveOutDate = isShowCounterOffer(row)
          ? row.counterOffers?.[0]?.moveOutDate
          : row?.moveOutDate;

        return moveOutDate ? formatDay(dayFormatType.CONTENTDATE, moveOutDate) : '-';
      },
    },
  ];

  const [queryOrder, { loading }] = useLazyQuery(QUERY_DIFFERENT_STAGE_ORDERS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onError: e => {
      message.error(e.toString());
    },
    onCompleted: result => {
      if (result?.viewer?.company?.orders) {
        formatOrder(result.viewer.company.orders);
      }
    },
  });

  const getOrder = pageInfo => {
    const { current, pageSize } = pageInfo;

    const variables = {
      pageNumber: current,
      stages: 'WAITING_FOR_CHECK_IN',
      pageSize,
      sortBy: 'MOVE_IN_DATE',
    };

    setPageSize(pageSize);
    setCurrent(current);

    queryOrder({ variables });
  };

  const handleShowSizeChange = (_, size) => {
    getOrder({
      current: 1,
      pageSize: size,
    });
  };

  return (
    <Row className={classNames(classes.row, { [classes.more]: !!totalCount })}>
      <div className={classes.title}>
        <span className={classes.circleIcon} />

        <span className={classes.pendingBoxTitle}>{t('board.pending_checkIn.title')}</span>
      </div>
      <If condition={!totalCount}>
        <Col>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('board.empty.pending_check_in.dec')}
          />
        </Col>
      </If>
      <If condition={totalCount}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={orderInfo}
          loading={loading}
          onChange={getOrder}
          pagination={{
            current,
            pageSize,
            showSizeChanger: true,
            hideOnSinglePage: false,
            total: totalCount,
            onShowSizeChange: handleShowSizeChange,
          }}
          onRow={record => {
            return {
              onClick: () => {
                openOrderDetailsPage(record);
              },
            };
          }}
        />
      </If>
    </Row>
  );
};
export default PendingCheckIn;
